import * as React from "react";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Button from "@amzn/awsui-components-react/polaris/button";
import Header from "@amzn/awsui-components-react/polaris/header";
import { setAsinWithListOfMarketplaces } from "./stamperWizardConnector";
import {
  Alert,
  Container,
  FormField,
  Icon,
  Multiselect,
  Pagination,
  Table,
  TableProps,
  Textarea,
} from "@amzn/awsui-components-react";

import { useEffect, useState } from "react";
import { AsinWithListOfMarketplaces } from "./StamperWizard";
import CollectionPreferences from "@amzn/awsui-components-react/polaris/collection-preferences";
import { MARKETPLACE_MULTI_SELECT_ITEMS } from "../searchSideBar/SearchSideBar-config";

type StamperASINImportProps = {
  dispatch: any;
  touched: { [key: string]: boolean };
  asinsWithListOfMarketplaces: AsinWithListOfMarketplaces[];
};

function createAsinsWithListOfMarketplaces(
  asinMp: AsinWithListOfMarketplaces
): {
  asin: string;
  marketplaces: string[];
} {
  const newAsinMp = { asin: "", marketplaces: [""] };
  if (asinMp.asin) {
    newAsinMp.asin = asinMp.asin;
  }
  if (asinMp.marketplaces) {
    newAsinMp.marketplaces = asinMp.marketplaces;
  }
  return newAsinMp;
}

export const StamperASINImport = ({
  dispatch,
  touched,
  asinsWithListOfMarketplaces,
}: StamperASINImportProps) => {
  const [asinsText, setAsinsText] = useState<string>("");
  const [touchedAsinBox, setTouchedAsinBox] = useState<boolean>(false);
  const [asinsInvalid, setAsinsInvalid] = useState<boolean>(true);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [asinMarketplacesValid, setAsinMarketplacesValid] =
    useState<boolean>(false);
  useEffect(() => {
    asinMarketplacesSelected(asinsWithListOfMarketplaces);
  }, [asinsWithListOfMarketplaces]);

  const columnDefinitions: TableProps.ColumnDefinition<AsinWithListOfMarketplaces>[] =
    [
      {
        id: "asin",
        header: "ASIN",
        width: "15%",
        cell: (item) => item.asin,
      },
      {
        id: "marketplaces",
        header: "Marketplaces",
        width: "15%",
        editConfig: {
          ariaLabel: "result",
          editIconAriaLabel: "editable",
          editingCell: (item, { setValue }) => {
            const value = item.marketplaces;
            return (
              <Multiselect
                autoFocus={true}
                hideTokens={true}
                expandToViewport={true}
                selectedOptions={value.map((mp) => {
                  return { label: mp, value: mp };
                })}
                onChange={(event) => {
                  setValue(event.detail.selectedOptions);
                  const updatedAsinMp =
                    asinsWithListOfMarketplaces?.map((asinMp) => {
                      if (item.asin != asinMp.asin) {
                        return {
                          asin: asinMp.asin,
                          marketplaces: asinMp.marketplaces,
                        };
                      } else {
                        const updatedMarketplaces =
                          event.detail.selectedOptions.map((so) => {
                            return so.value || "";
                          });
                        return {
                          asin: item.asin,
                          marketplaces: updatedMarketplaces,
                        };
                      }
                    }) || [];
                  dispatch(
                    setAsinWithListOfMarketplaces({
                      asinsWithListOfMarketplaces: updatedAsinMp,
                    })
                  );
                  asinMarketplacesSelected(updatedAsinMp);
                }}
                options={MARKETPLACE_MULTI_SELECT_ITEMS}
                placeholder="Choose options"
              />
            );
          },
        },
        cell: (item) => {
          return (
            <div>
              {touched["1"] && item.marketplaces.length == 0 ? (
                <span>
                  <Icon size="small" variant="error" name="status-negative" />{" "}
                  <i> please select marketplaces </i>
                </span>
              ) : (
                item.marketplaces.join(", ")
              )}
            </div>
          );
        },
      },
      {
        id: "remove",
        header: "Remove",
        width: "2%",
        cell: (item) => {
          return (
            <Button
              variant={"inline-icon"}
              iconName={"close"}
              onClick={() => {
                removeAsin(item.asin);
                asinMarketplacesSelected(asinsWithListOfMarketplaces);
              }}
            />
          );
        },
      },
    ];

  const removeAsin = (asin: string) => {
    dispatch(
      setAsinWithListOfMarketplaces({
        asinsWithListOfMarketplaces: asinsWithListOfMarketplaces.filter(
          (asinWIthListOfMarketplaces) =>
            asinWIthListOfMarketplaces.asin != asin
        ),
      })
    );
  };

  const validateAsins = (asinList: string[]) => {
    const asinRegex = "\\d{9}[\\dX]|[A-Z][A-Z\\d]{9}";
    const regex = RegExp(asinRegex);
    if (
      asinList == undefined ||
      asinList.length == 0 ||
      asinList.some((asin) => !regex.test(asin) || asin.length != 10)
    ) {
      setAsinsInvalid(true);
      return false;
    } else {
      setAsinsInvalid(false);
      return true;
    }
  };

  const asinMarketplacesSelected = (
    asinsWithListOfMarketplaces: AsinWithListOfMarketplaces[]
  ) => {
    if (
      asinsWithListOfMarketplaces.length == 0 ||
      asinsWithListOfMarketplaces.some(
        (asinWithListOfMarketplaces) =>
          asinWithListOfMarketplaces.marketplaces.length == 0
      )
    ) {
      setAsinMarketplacesValid(false);
    } else {
      setAsinMarketplacesValid(true);
    }
  };

  const handleImportAsins = () => {
    setTouchedAsinBox(true);
    const asinsList = Array.from(
      new Set(asinsText.split(/[, \n]+/).filter((asin) => asin != ""))
    );

    if (validateAsins(asinsList)) {
      const temp =
        asinsList?.map((asin: string) =>
          createAsinsWithListOfMarketplaces({
            asin: asin,
            marketplaces: [],
          })
        ) || [];
      setAsinsText("");
      setTouchedAsinBox(false);
      const newAsinMps = Array.from(new Set(temp));
      const setOfExistingAsins = new Set(
        asinsWithListOfMarketplaces.map((asinMpList) => asinMpList.asin)
      );
      const newAsinMpsNotInTable = newAsinMps.filter(
        (asinMp) => !setOfExistingAsins.has(asinMp.asin)
      );
      const combinedAsinMps =
        asinsWithListOfMarketplaces.concat(newAsinMpsNotInTable);
      dispatch(
        setAsinWithListOfMarketplaces({
          asinsWithListOfMarketplaces: combinedAsinMps,
        })
      );
      asinMarketplacesSelected(combinedAsinMps);
    }
  };
  return (
    <div>
      <Container>
        <SpaceBetween size={"l"}>
          <FormField
            label={<>ASIN Input</>}
            constraintText="Each ASIN must be a length of 10 and contain only alphanumberic characters"
            stretch={true}
          >
            <Alert statusIconAriaLabel="Info">
              Enter one or more ASINs in this box and click ’Add ASIN’ to
              proceed. ASINs can be separated by comma, space, or line break.
              Any duplicated ASIN will be automatically de-duped in table below
            </Alert>
            <Textarea
              placeholder="ASIN1, ASIN2, ASIN3"
              value={asinsText}
              rows={6}
              invalid={asinsText.trim() != "" && asinsInvalid && touchedAsinBox}
              onChange={({ detail }) => {
                setAsinsText(detail.value);
              }}
            />
          </FormField>
          <div style={{ float: "right" }}>
            <Button onClick={handleImportAsins}>Add ASIN</Button>
          </div>
        </SpaceBetween>
      </Container>
      <Container>
        <SpaceBetween size={"l"}>
          <FormField label={<>ASIN/Marketplace</>} stretch={true}>
            <Alert statusIconAriaLabel="Info">
              Select the applicable Marketplace(s) per ASIN that you wish to
              contribute to. ASIN must be created and in Catalog prior to
              continuing. You must select at least one Marketplace per ASIN to
              submit a contribution
            </Alert>
            <FormField
              stretch={true}
              errorText={
                touched["1"] && asinsWithListOfMarketplaces.length == 0
                  ? "Please Add ASIN into table"
                  : touched["1"] && !asinMarketplacesValid
                  ? "ASINs Need at least 1 Marketplace selected. Make sure to check all pages"
                  : undefined
              }
            >
              <Table
                enableKeyboardNavigation={true}
                header={
                  asinsWithListOfMarketplaces.length != 0 ? (
                    <Header variant={"h3"}>Added ASINs</Header>
                  ) : undefined
                }
                columnDefinitions={columnDefinitions}
                items={asinsWithListOfMarketplaces.slice(
                  pageNumber * pageSize - pageSize,
                  pageNumber * pageSize
                )}
                pagination={
                  <Pagination
                    currentPageIndex={pageNumber}
                    pagesCount={Math.ceil(
                      asinsWithListOfMarketplaces.length / pageSize
                    )}
                    onChange={({ detail }) =>
                      setPageNumber(detail.currentPageIndex)
                    }
                  />
                }
                preferences={
                  <CollectionPreferences
                    onConfirm={(event) => {
                      setPageSize(event.detail.pageSize || 10);
                    }}
                    title="Preferences"
                    confirmLabel="Confirm"
                    cancelLabel="Cancel"
                    preferences={{
                      pageSize: pageSize,
                    }}
                    pageSizePreference={{
                      title: "Page size",
                      options: [
                        { value: 10, label: "10 records" },
                        { value: 20, label: "20 records" },
                        { value: 50, label: "50 records" },
                      ],
                    }}
                  />
                }
              />
            </FormField>
          </FormField>
        </SpaceBetween>
      </Container>
    </div>
  );
};
